import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, Play, ChevronRight, Calendar, Tv, ArrowUpRight, Clock, Star, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import LiveStreamSchedule from "@/components/LiveStreamSchedule";
import KingdomHourSection from "@/components/tv/KingdomHourSection";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ---------------------------------------------------------------------------
// Small reusable atoms
// ---------------------------------------------------------------------------
const LivePulse = () => (
  <span className="relative inline-flex w-2.5 h-2.5">
    <span className="absolute inline-flex w-full h-full rounded-full bg-red-500 opacity-75 animate-ping" />
    <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-red-500" />
  </span>
);

const formatHour = (h) => {
  const hh = ((h % 24) + 24) % 24;
  if (hh === 0) return "12 AM";
  if (hh === 12) return "12 PM";
  return hh < 12 ? `${hh} AM` : `${hh - 12} PM`;
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function TV() {
  const [settings, setSettings] = useState(null);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [shows, setShows] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(() => {
    // Default to today (JS: 0=Sun..6=Sat → convert to Mon=0..Sun=6)
    const js = new Date().getDay();
    return js === 0 ? 6 : js - 1;
  });

  useEffect(() => {
    Promise.all([
      api.get("/tv/settings"),
      api.get("/tv/now-playing"),
      api.get("/tv/shows"),
      api.get("/tv/episodes?limit=12"),
      api.get("/tv/schedule"),
    ]).then(([s, np, sh, ep, sc]) => {
      setSettings(s.data);
      setNowPlaying(np.data);
      setShows(sh.data || []);
      setEpisodes(ep.data || []);
      setSchedule(sc.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Refresh "Now Playing" every 60s so the ticker stays honest
  useEffect(() => {
    const id = setInterval(() => {
      api.get("/tv/now-playing").then(({ data }) => setNowPlaying(data)).catch(() => {});
    }, 60000);
    return () => clearInterval(id);
  }, []);

  const featuredEpisodes = useMemo(() => episodes.filter((e) => e.is_featured), [episodes]);
  const dayBlocks = useMemo(() => schedule
    .filter((b) => b.day === selectedDay)
    .sort((a, b) => a.start_hour - b.start_hour), [schedule, selectedDay]);

  if (loading) {
    return (
      <div className="min-h-[70vh] bg-ink flex items-center justify-center" data-testid="tv-loading">
        <Loader2 size={26} className="animate-spin text-gold-400 mr-3" />
        <span className="text-white/60 uppercase tracking-[0.22em] text-sm">Tuning in…</span>
      </div>
    );
  }

  return (
    <div data-testid="tv-page" className="bg-ink text-white">
      {/* ============================================================== */}
      {/*  Channel bar — brand + tagline + marquee                        */}
      {/* ============================================================== */}
      <div className="relative border-b border-gold-500/25 bg-ink-royal/40 overflow-hidden pt-24 md:pt-28" data-testid="tv-channel-bar">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-5 flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-3">
            <Tv size={22} className="text-gold-500" strokeWidth={1.5} />
            <div>
              <p className="font-serif text-xl md:text-2xl text-white leading-none">
                <span className="text-gold-gradient">HBTV</span> <span className="text-white/70 text-sm md:text-base align-middle">· House of Blessing TV</span>
              </p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.28em] text-gold-400">
                {settings?.tagline} · <span className="text-white/60">{settings?.timezone_label || "WAT · Cameroon"}</span>
              </p>
            </div>
          </div>
          {/* Live 24/7 CTA */}
          <Link
            to="/tv/live"
            data-testid="tv-live-cta"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-4 py-2 text-[11px] uppercase tracking-[0.22em] font-semibold transition-colors shrink-0"
          >
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex w-full h-full rounded-full bg-white opacity-75 animate-ping" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-white" />
            </span>
            Watch Live 24/7
          </Link>
          {settings?.youtube_channel_url && (
            <a
              href={settings.youtube_channel_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tv-youtube-link"
              className="inline-flex items-center gap-2 border border-red-500/60 hover:border-red-400 text-red-300 hover:text-red-200 px-3 py-2 text-[10px] uppercase tracking-[0.22em] transition-colors shrink-0"
            >
              YouTube Channel
            </a>
          )}
          {/* Marquee */}
          <div className="hidden md:flex flex-1 items-center min-w-0 border-l border-gold-500/25 pl-6">
            <div className="w-full overflow-hidden whitespace-nowrap">
              <motion.p
                animate={{ x: ["100%", "-100%"] }}
                transition={{ duration: 30, ease: "linear", repeat: Infinity }}
                className="inline-block text-[11px] uppercase tracking-[0.25em] text-white/60"
              >
                {settings?.marquee_message} &nbsp;·&nbsp; Broadcasting from the House of Blessing to the ends of the earth. &nbsp;·&nbsp; Faith · Leadership · Business · Innovation
              </motion.p>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================== */}
      {/*  Now Playing hero                                                */}
      {/* ============================================================== */}
      <section className="relative overflow-hidden" data-testid="tv-hero">
        {/* Background poster */}
        <div className="absolute inset-0">
          {nowPlaying?.show?.cover_image && (
            <img
              src={nowPlaying.show.cover_image}
              alt=""
              className="w-full h-full object-cover object-center opacity-40"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/60 to-transparent" />
        </div>

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="grid gap-10 lg:grid-cols-12 items-center">
            {/* Left: On-air info */}
            <div className="lg:col-span-7 max-w-2xl">
              {/* ON AIR pill */}
              <div className="inline-flex items-center gap-2.5 border border-gold-500/40 bg-ink/70 backdrop-blur px-4 py-2 rounded-full mb-6" data-testid="tv-onair-pill">
                <LivePulse />
                <span className="text-[11px] uppercase tracking-[0.28em] text-white/90 font-semibold">
                  {nowPlaying?.mode === "live" ? "Live on Air" : "On Air Now"}
                </span>
                {nowPlaying?.block && (
                  <span className="text-[11px] uppercase tracking-[0.22em] text-gold-400">
                    · {formatHour(nowPlaying.block.start_hour)} → {formatHour(nowPlaying.block.end_hour)}
                  </span>
                )}
              </div>

              {nowPlaying?.show && (
                <p className="text-[11px] uppercase tracking-[0.3em] text-gold-400 mb-3" data-testid="tv-hero-show">
                  {nowPlaying.show.category} · {nowPlaying.show.title}
                </p>
              )}

              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-white" data-testid="tv-hero-title">
                {nowPlaying?.episode?.title || nowPlaying?.live_title || "Broadcasting soon"}
              </h1>

              {nowPlaying?.episode?.description && (
                <p className="mt-5 md:mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-xl">
                  {nowPlaying.episode.description}
                </p>
              )}

              <div className="mt-9 flex flex-wrap items-center gap-3">
                {nowPlaying?.episode?.id ? (
                  <Link
                    to={`/tv/watch/${nowPlaying.episode.id}`}
                    data-testid="tv-hero-play"
                    className="group inline-flex items-center gap-2.5 bg-gold-500 hover:bg-gold-400 text-ink font-semibold uppercase tracking-[0.14em] text-sm px-8 py-4 transition-[background,transform,box-shadow] duration-300 active:scale-[0.98] shadow-[0_25px_60px_-20px_rgba(212,175,55,0.65)]"
                  >
                    <Play size={16} className="fill-current" strokeWidth={0} /> Watch Now
                  </Link>
                ) : null}
                {nowPlaying?.show?.slug && (
                  <Link
                    to={`/tv/show/${nowPlaying.show.slug}`}
                    data-testid="tv-hero-show-link"
                    className="inline-flex items-center gap-2 border border-gold-500/40 hover:border-gold-400 text-white/85 hover:text-gold-300 uppercase tracking-[0.14em] text-sm px-6 py-4 transition-colors duration-300"
                  >
                    About this show <ArrowUpRight size={15} />
                  </Link>
                )}
              </div>

              {nowPlaying?.episode?.duration && (
                <p className="mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/40">
                  <Clock size={12} /> Episode runtime · {nowPlaying.episode.duration}
                </p>
              )}
            </div>

            {/* Right: Poster card */}
            <div className="lg:col-span-5">
              {nowPlaying?.episode?.thumbnail && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                  className="relative aspect-video overflow-hidden border border-gold-500/30 shadow-[0_60px_120px_-40px_rgba(11,32,83,0.85)]"
                >
                  <img src={nowPlaying.episode.thumbnail} alt={nowPlaying.episode.title} className="w-full h-full object-cover" />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
                  {nowPlaying?.episode?.id && (
                    <Link
                      to={`/tv/watch/${nowPlaying.episode.id}`}
                      aria-label="Play"
                      data-testid="tv-hero-play-card"
                      className="absolute inset-0 flex items-center justify-center group"
                    >
                      <span className="relative flex items-center justify-center w-20 h-20 md:w-24 md:h-24">
                        <span className="absolute inset-0 rounded-full bg-gold-500/20 animate-ping" />
                        <span className="relative w-16 h-16 md:w-20 md:h-20 rounded-full bg-gold-500 text-ink flex items-center justify-center shadow-[0_20px_60px_-15px_rgba(212,175,55,0.7)] transition-transform duration-300 group-hover:scale-105">
                          <Play size={26} className="ml-1 fill-current" strokeWidth={0} />
                        </span>
                      </span>
                    </Link>
                  )}
                  <div className="absolute left-4 right-4 bottom-3 text-[11px] uppercase tracking-[0.24em] text-white/70">
                    {nowPlaying?.show?.title}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/*  Live Stream Schedule (WAT with visitor-local conversion)        */}
      {/* ============================================================== */}
      <LiveStreamSchedule />

      {/* ============================================================== */}
      {/*  Kingdom Hour — 12-part flagship series                          */}
      {/* ============================================================== */}
      <KingdomHourSection />

      {/* ============================================================== */}
      {/*  Shows carousel                                                  */}
      {/* ============================================================== */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-20" data-testid="tv-shows">
        <Reveal className="mb-10 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-gold-500 mb-3">The Networks</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight">Our <span className="font-accent italic text-gold-gradient">Shows</span></h2>
          </div>
          <p className="text-white/50 text-sm max-w-md">Six branded series, one Kingdom message. Every hour of the day, something on HBTV is speaking your language.</p>
        </Reveal>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {shows.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.06}>
              <Link
                to={`/tv/show/${s.slug}`}
                data-testid={`tv-show-${s.slug}`}
                className="group relative block aspect-[4/5] overflow-hidden border border-gold-500/20 bg-ink-paper"
              >
                <img
                  src={s.cover_image}
                  alt={s.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
                {/* Accent bar */}
                <span className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: s.accent_color || "#D4AF37" }} />
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold-400">{s.category}</p>
                  <p className="mt-2 font-serif text-2xl md:text-3xl text-white leading-tight">{s.title}</p>
                  <p className="mt-2 text-white/70 text-sm leading-relaxed line-clamp-2">{s.tagline}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-gold-500 group-hover:gap-2.5 transition-all duration-300">
                    Enter show <ChevronRight size={13} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============================================================== */}
      {/*  Featured episodes strip                                         */}
      {/* ============================================================== */}
      {featuredEpisodes.length > 0 && (
        <section className="border-t border-gold-500/15 py-16 md:py-20" data-testid="tv-featured">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <Reveal className="mb-10 flex items-end justify-between gap-4 flex-wrap">
              <div>
                <p className="text-[11px] uppercase tracking-[0.28em] text-gold-500 mb-3 flex items-center gap-2"><Star size={12} /> Editor's Picks</p>
                <h2 className="font-serif text-3xl md:text-5xl leading-tight">Featured <span className="font-accent italic text-gold-gradient">episodes</span></h2>
              </div>
            </Reveal>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {featuredEpisodes.slice(0, 6).map((e, i) => {
                const show = shows.find((s) => s.slug === e.show_slug);
                return (
                  <Reveal key={e.id} delay={i * 0.05}>
                    <Link
                      to={`/tv/watch/${e.id}`}
                      data-testid={`tv-featured-${e.id}`}
                      className="group block border border-gold-500/20 bg-ink-paper hover:border-gold-400 transition-colors duration-300"
                    >
                      <div className="relative aspect-video overflow-hidden">
                        <img src={e.thumbnail} alt={e.title} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]" />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <span className="w-14 h-14 rounded-full bg-gold-500/90 text-ink flex items-center justify-center">
                            <Play size={20} className="ml-0.5 fill-current" strokeWidth={0} />
                          </span>
                        </div>
                        {e.duration && (
                          <span className="absolute bottom-3 right-3 bg-ink/80 backdrop-blur border border-gold-500/30 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-white/80">
                            {e.duration}
                          </span>
                        )}
                      </div>
                      <div className="p-5">
                        <p className="text-[10px] uppercase tracking-[0.28em] text-gold-400">{show?.title || e.show_slug}</p>
                        <p className="mt-2 font-serif text-white text-xl leading-tight line-clamp-2">{e.title}</p>
                        <p className="mt-2 text-sm text-white/60 leading-relaxed line-clamp-2">{e.description}</p>
                      </div>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============================================================== */}
      {/*  Weekly schedule                                                 */}
      {/* ============================================================== */}
      <section className="border-t border-gold-500/15 py-16 md:py-24" data-testid="tv-schedule-section">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal className="mb-10">
            <p className="text-[11px] uppercase tracking-[0.28em] text-gold-500 mb-3 flex items-center gap-2"><Calendar size={12} /> Programming</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight">This <span className="font-accent italic text-gold-gradient">week</span> on HBTV</h2>
          </Reveal>

          {/* Day tabs */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="tv-schedule-days">
            {DAYS.map((d, i) => {
              const active = i === selectedDay;
              return (
                <button
                  key={d}
                  onClick={() => setSelectedDay(i)}
                  data-testid={`tv-day-${i}`}
                  className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] border transition-colors duration-300 ${
                    active
                      ? "border-gold-400 bg-gold-500/12 text-gold-200"
                      : "border-white/12 bg-ink-paper/40 text-white/60 hover:border-gold-500/50 hover:text-gold-300"
                  }`}
                >
                  {d}
                </button>
              );
            })}
          </div>

          {/* Blocks for selected day */}
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedDay}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="border border-gold-500/20 divide-y divide-gold-500/10"
              data-testid={`tv-schedule-${selectedDay}`}
            >
              {dayBlocks.length === 0 && (
                <div className="p-6 text-center text-white/50 text-sm">Off-air on this day.</div>
              )}
              {dayBlocks.map((b, i) => {
                const show = shows.find((s) => s.slug === b.show_slug);
                return (
                  <Link
                    key={b.id || i}
                    to={show ? `/tv/show/${show.slug}` : "#"}
                    data-testid={`tv-block-${b.id || i}`}
                    className="group flex items-center gap-5 md:gap-8 p-4 md:p-5 hover:bg-ink-paper/50 transition-colors duration-200"
                  >
                    <div className="w-24 md:w-32 shrink-0 text-left">
                      <p className="text-[11px] uppercase tracking-[0.24em] text-gold-500">{formatHour(b.start_hour)}</p>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-0.5">→ {formatHour(b.end_hour)}</p>
                    </div>
                    {show?.cover_image && (
                      <div className="hidden sm:block relative w-16 h-16 md:w-20 md:h-20 shrink-0 overflow-hidden border border-gold-500/25">
                        <img src={show.cover_image} alt="" className="w-full h-full object-cover" />
                        <span className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: show.accent_color || "#D4AF37" }} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-serif text-white text-xl md:text-2xl leading-tight line-clamp-1">{b.label || show?.title}</p>
                      {show?.tagline && <p className="text-white/50 text-sm leading-snug line-clamp-1">{show.tagline}</p>}
                    </div>
                    <ChevronRight size={18} className="text-gold-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ============================================================== */}
      {/*  Closing CTA                                                     */}
      {/* ============================================================== */}
      <section className="border-t border-gold-500/15 py-24 md:py-32 text-center" data-testid="tv-closing">
        <div className="mx-auto max-w-2xl px-6">
          <p className="text-[11px] uppercase tracking-[0.28em] text-gold-500 mb-5 flex items-center justify-center gap-2">
            <Radio size={12} /> Broadcasting from the House of Blessing
          </p>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight text-white">
            Never miss a <span className="font-accent italic text-gold-gradient">broadcast.</span>
          </h2>
          <p className="mt-5 text-white/60 leading-relaxed">
            Subscribe to the daily devotional and be first to know when new episodes drop, live services go on air, and Praise Nights start.
          </p>
          <Link
            to="/#home-newsletter"
            className="mt-8 inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-ink font-semibold uppercase tracking-[0.14em] text-sm px-8 py-4 transition-colors"
            data-testid="tv-subscribe-cta"
          >
            Subscribe to Alerts <ArrowUpRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
}
